import { Box, Chip, Paper, Skeleton, Tab, Tabs, Typography } from '@mui/material'
import { ArrowForwardIos } from '@mui/icons-material'
import { useEffect, useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { LongTermService } from '../../services/buyhold.service'
import { LongTerm, LongTermStock } from '../../_types/longterm.type'
import { useError } from '../../context/error'
import { Formatters } from '../../_helpers/formatter.helper'
import { filterRecentBuys, filterRecentSells, gainPct } from '../../_helpers/longterm.helper'
import TickerLink from '../ticker-link'
import TickerIcon from '../ticker-icon'

function formatShortDate(d: string | undefined): string {
  if (!d) return ''
  const t = new Date(d)
  if (!Number.isFinite(t.getTime())) return ''
  return t.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

function GainCell({ pct }: { pct: number | null }) {
  if (pct == null) {
    return <Typography variant='caption' color='text.secondary'>—</Typography>
  }
  return (
    <Chip
      label={Formatters.formatPercentage(pct)}
      size='small'
      color={pct >= 0 ? 'success' : 'error'}
      variant='outlined'
    />
  )
}

function Row({
  symbol,
  middle,
  pct,
  isLast
}: {
  symbol: string
  middle: string
  pct: number | null
  isLast: boolean
}) {
  return (
    <Box
      display='flex'
      alignItems='center'
      justifyContent='space-between'
      py={0.75}
      gap={1}
      sx={{ borderBottom: isLast ? 0 : 1, borderColor: 'divider' }}
    >
      <Box display='flex' alignItems='center' gap={1}>
        <TickerIcon symbol={symbol} />
        <TickerLink display='link' ticker={symbol} />
      </Box>
      <Typography variant='caption' color='text.secondary' sx={{ flex: 1, textAlign: 'right', mr: 1 }}>
        {middle}
      </Typography>
      <GainCell pct={pct} />
    </Box>
  )
}

export default function RecentLongTermBuysSells() {
  const { setError } = useError()
  const [longTerm, setLongTerm] = useState<LongTerm | null>(null)
  const [loading, setLoading] = useState(true)
  const [activeIdx, setActiveIdx] = useState(0)

  useEffect(() => {
    const longTermService = new LongTermService(setError)
    longTermService.getCurrentBuyHold().then(res => {
      if (res?.longTerm) setLongTerm(res.longTerm)
      setLoading(false)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const stocks = longTerm?.stocks ?? []
  const buys = filterRecentBuys(stocks)
  const sells = filterRecentSells(stocks)

  const showingBuys = activeIdx === 0
  const activeList = showingBuys ? buys : sells
  const emptyMsg = showingBuys
    ? 'No long term buys in the last 7 days.'
    : 'No long term sells in the last 7 days.'

  return (
    <Paper elevation={3} sx={{ p: 2 }}>
      <Box display='flex' alignItems='center' justifyContent='space-between'>
        <Typography variant='h6'>Recent Long Term Trades</Typography>
        <RouterLink to='/longterm' style={{ textDecoration: 'none' }}>
          <Typography
            variant='caption'
            color='primary'
            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
          >
            View all <ArrowForwardIos sx={{ fontSize: 10 }} />
          </Typography>
        </RouterLink>
      </Box>

      {loading ? (
        <>
          <Skeleton variant='rectangular' height={32} sx={{ my: 1, borderRadius: 1 }} />
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} variant='text' height={28} />
          ))}
        </>
      ) : (
        <>
          <Tabs
            value={activeIdx}
            onChange={(_, v) => setActiveIdx(v)}
            sx={{ minHeight: 36, mt: 1, '.MuiTab-root': { minHeight: 36, py: 0.5 } }}
          >
            <Tab label={`Recent Buys${buys.length ? ` (${buys.length})` : ''}`} />
            <Tab label={`Recent Sells${sells.length ? ` (${sells.length})` : ''}`} />
          </Tabs>

          <Box mt={1}>
            {activeList.length === 0 ? (
              <Typography variant='body2' color='text.secondary'>
                {emptyMsg}
              </Typography>
            ) : (
              activeList.map((stock: LongTermStock, idx: number) => (
                <Row
                  key={`${showingBuys ? 'buy' : 'sell'}-${stock.symbol}`}
                  symbol={stock.symbol}
                  middle={
                    showingBuys
                      ? `Entered ${formatShortDate(stock.entryDate)}`
                      : `Sold ${formatShortDate(stock.soldDate)} · entered ${formatShortDate(stock.entryDate)}`
                  }
                  pct={
                    showingBuys
                      ? (stock.skipUpdate ? null : gainPct(stock.entryPX, stock.currentPX))
                      : gainPct(stock.entryPX, stock.soldAt)
                  }
                  isLast={idx === activeList.length - 1}
                />
              ))
            )}
          </Box>
        </>
      )}
    </Paper>
  )
}
